import { CareMapState } from '../../caremap/CareMapState';
import { ICareMap, ICareMapSection } from '../../caremap/ICareMap';
import { MixPanelEventEmitter } from '../../metrics/MixPanelEventEmitter';
import { CommunityModelFactory } from '../../share-caremap/CommunityModelFactory';
import { HistoryViewModel } from '../../support/HistoryViewModel';
import { IUploadCompleteInfo } from '../generic-notes/attachments/FileUploader';
import { INoteTarget } from '../generic-notes/INoteTarget';
import { ICaremapNote, ICaremapNoteResponse } from './NotesClient';
import { IRecording } from '../../recording/RecordingState';
import { NetworkAdapter } from '../../support/NetworkAdapter';
import { isPlatform } from '../../utils/isPlatform';
import { TasksState } from '../../tasks/TasksState';
import { NotePinsState } from '../pins/NotePinsState';
import { INoteInteractionSurface } from '../reactions/INoteInteractionSurface';


export class CareMapNoteTracker {
    static inject =() => [CareMapState, MixPanelEventEmitter, HistoryViewModel, CommunityModelFactory, NetworkAdapter, TasksState, NotePinsState];
    constructor(
        private careMapState: CareMapState,
        private tracking: MixPanelEventEmitter,
        private history: HistoryViewModel,
        private communityModelFactory: CommunityModelFactory,
        private networkAdapter: NetworkAdapter,
        private tasksState: TasksState,
        private notePinsState: NotePinsState
    ){}

    onNoteViewed = (note: ICaremapNoteResponse, trackingFrom?: string) => {
        this.tracking.trackWithDebounce({
            event: 'Note Viewed',
            getContext: () => this.getNoteTrackingContextV2(note, 'view', trackingFrom)
        });
    };

    onAddedNote = (note: ICaremapNoteResponse, trackingFrom?: string) => {
        this.tracking.trackWithDebounce({
            event: 'Note Added',
            getContext: () => this.getNoteTrackingContextV2(note, 'add', trackingFrom)
        });
    };

    onUpdatedNote = (note: ICaremapNoteResponse, trackingFrom?: string) => {
        this.tracking.trackWithDebounce({
            event: 'Note Edited',
            getContext: () => this.getNoteTrackingContextV2(note, 'edit', trackingFrom)
        });
    };

    // A comment is a note under a note, so it carries the note context, plus the surface it was made on.
    onCommentChanged = ({ comment, parentNote, action, surface }: { comment: ICaremapNoteResponse; parentNote: ICaremapNote; action: 'add' | 'edit' | 'delete'; surface: INoteInteractionSurface; }) => {
        const events = { add: 'Community Comment Added', edit: 'Community Comment Edited', delete: 'Community Comment Deleted' };
        this.tracking.trackWithDebounce({
            event: events[action],
            getContext: () => ({
                ...this.getNoteTrackingContextV2(comment, action),
                'Surface': surface,
                'Note Private': Boolean(parentNote.isPrivate)
            })
        });
    };

    onDeletedNote = (deletedNote: ICaremapNoteResponse, trackingFrom?: string) => {
        this.tracking.trackWithDebounce({
            event: 'Note Deleted',
            getContext: () => this.getNoteTrackingContextV2(deletedNote, 'delete', trackingFrom)
        });
    };

    onFileUploaded = (fileInfo: IUploadCompleteInfo, noteTarget: INoteTarget) => {
        this.tracking.track(
            'Note File Uploaded',
            () => {
                return {
                    location: this.getNoteTrackingLocation(noteTarget),
                    type: fileInfo.key.name.substring(fileInfo.key.name.lastIndexOf('.')) || ''
                };
            }
        );
    };

    onNotePinned = (note: ICaremapNoteResponse, trackingFrom?: string) => {
        this.tracking.track('Note Pinned', () => ({
            ...this.getPinTrackingContext(note, trackingFrom),
            'Total Pins Count': this.notePinsState.getPinsCount()
        }));
    };

    onNoteUnpinned = (note: ICaremapNoteResponse, trackingFrom?: string) => {
        this.tracking.track('Note Unpinned', () => ({
            ...this.getPinTrackingContext(note, trackingFrom),
            'Total Pins Count': this.notePinsState.getPinsCount()
        }));
    };

    private getPinTrackingContext = (note: ICaremapNoteResponse, from?: string) => {
        const noteTarget = note.otherAttributes.noteTarget;

        if (!from) {
            from = this.getTrackingFromLocation();
        }

        return {
            'Note Id': note.noteId,
            location: this.getNoteTrackingLocation(noteTarget),
            'Has Recording': !!note.otherAttributes.recording,
            'Has AI Summary': !!note.otherAttributes.recording?.summary,
            '# of attached files': note.attachments.length,
            'Tags Count': note.tags?.length || 0,
        };
    };

    onFileRemoved = (noteTarget: INoteTarget, url: string) => {
        this.tracking.trackWithDebounce({
            event: 'Note File Removed',
            getContext: () => {
                return {
                    location: this.getNoteTrackingLocation(noteTarget),
                    type: url.substring(url.lastIndexOf('.')) || ''
                };
            }
        });
    };

    private getNoteTrackingContextV2 = (
        note: ICaremapNoteResponse,
        action: 'view' | 'add' | 'edit' | 'delete',
        from?: string
    ) => {
        const community = this.communityModelFactory.forActiveCaremap();
        const activeCaremap = this.careMapState.getActiveCaremap() as ICareMap | undefined;

        if (!activeCaremap){
            throw new Error('expected an active caremap');
        }

        const noteTarget = note.otherAttributes.noteTarget;
        // the api stamps the author's role on notes written since contributors exist; older notes
        // fall back to the owner-or-not guess these properties always made
        const authorRole = note.createdByUser?.role || (note.createdBy === community.getOwner().userId ? 'owner' : 'co-owner');

        const section = activeCaremap[noteTarget.section as ICareMapSection];
        let node = section;
        if (noteTarget.memberId) {
            // a reader without the care details gets every section's members as [] (the api strips them)
            node = (section as any).members.find((m: any) => m.id === noteTarget.memberId);
        }
        const type = node ? ((node as any).relationshipId || (node as any).relationship || (node as any).roleId || (node as any).role || '') : '';

        // TODO: don't love being tied to path
        const path = this.history.getCurrentLocation().pathname;

        if (!from) {
            from = path.includes('/caremaps/') ? 'Care Map' :
                path.includes('/notes/') ? 'Notes Page' :
                    'Caregiver Widget';
        }

        const recordingExtras: any = {};
        const recording = note.otherAttributes.recording;

        // For 'add' action, only include recording ID for linking events
        if (recording && action === 'add') {
            recordingExtras['Recording Id'] = recording.id;
        }

        if (recording && action !== 'add') { // view, edit and delete
            recordingExtras['Has Recording'] = true;
            recordingExtras['Has AI Summary'] = recording?.summary ? true : false;

            if (recording.msDuration) {
                const totalSeconds = Math.floor(recording.msDuration / 1000);
                const minutes = Math.floor(totalSeconds / 60);
                const seconds = totalSeconds % 60;
                recordingExtras['Recording Duration'] = `${minutes < 1 ? '' : minutes+'m' } ${seconds}s`;
            }

            // Count smart tasks associated with this note
            const tasks = this.tasksState.getTasks();
            const smartTasksCount = tasks.filter(task => {
                if (task.source?.noteId === note.noteId) {
                    return true;
                }
                if (recording?.id && task.source?.recordingId === recording.id) {
                    return true;
                }
                return false;
            }).length;
            recordingExtras['Smart Tasks Count'] = smartTasksCount;
        }

        // How the note was created, so reporting on Note Added doesn't need to join back to the
        // Note Creation Initiated event. Derived from the note itself: no recording -> typed;
        // a recording -> Voice unless it came from an uploaded file.
        if (action === 'add') {
            recordingExtras['Creation Method'] = recording ? (recording.isUpload ? 'Upload Audio' : 'Voice') : 'Type';
        }

        const actionLabel = { view: 'Viewed', add: 'Added', edit: 'Edited', delete: 'Deleted' }[action];

        return {
            'Note Id': note.noteId,
            type: type,
            location: this.getNoteTrackingLocation(noteTarget),
            hasTitle: note?.title !== '',
            private: note?.isPrivate as boolean,
            'Posted to Community': !note?.isPrivate,
            '# of attached files': note.attachments.length,
            'Tags Count': note.tags?.length || 0,
            [`${actionLabel} From`]: from,
            [`${actionLabel} By`]: authorRole,
            'Author Role': authorRole,
            'Is Pinned': this.notePinsState.isPinned(note.noteId),
            ...recordingExtras
        };
    };

    private getNoteTrackingLocation = (noteTarget: INoteTarget) => {
        const isPatient = noteTarget.section === 'patient';
        const isCheckin = noteTarget.section === 'checkin';
        const location = `${isPatient ? 'care recipient' : isCheckin ? 'daily log' : noteTarget.section}${noteTarget.memberId ? ' member' : ''}`;
        return location;
    };

    // Offline Recording Tracking Methods



    private _unused_getOfflineRecordingContext = (
        recording: IRecording,
        action: 'create' | 'sync' | 'sync-failed' | 'play',
        extras?: { [key: string]: any }
    ) => {
        const activeCaremap = this.careMapState.getActiveCaremap() as ICareMap | undefined;

        if (!activeCaremap) {
            throw new Error('expected an active caremap');
        }

        const noteTarget = { section: 'patient' } as const; // Offline recordings default to patient section

        const totalSeconds = Math.floor(recording.msDuration / 1000);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;

        const baseContext: { [key: string]: any } = {
            // Device & Network
            'Network Status': this.networkAdapter.isOnline ? 'online' : 'offline',

            // Recording Details
            'Recording Duration (ms)': `${minutes < 1 ? '' : minutes+'m' } ${seconds}s`,

            // Offline-specific
            'Storage Method': 'indexeddb',
            'Has Appointment': Boolean(recording.appointmentId),
            'Skip AI Processing': Boolean(recording.skipAIProcessing),

            // Care Context
            'location': this.getNoteTrackingLocation(noteTarget as any),

            // Action Context
            'Action': action
        };

        // Add action-specific context
        if (action === 'sync' && extras?.syncDuration) {
            baseContext['Sync Duration (ms)'] = extras.syncDuration;
        }

        if (action === 'sync-failed') {
            baseContext['Error Type'] = extras?.error || 'unknown';
            baseContext['Retry Count'] = extras?.retryCount || 0;
        }

        return baseContext;
    };


    private getDevicePlatform = (): string => {
        if (isPlatform('ios')) {
            return 'ios';
        }
        if (isPlatform('android')) {
            return 'android';
        }
        return 'web';
    };

    private getTrackingFromLocation = (): string => {
        const path = this.history.getCurrentLocation().pathname;

        if (path.includes('/caremaps/')) {
            return 'Care Map';
        }
        if (path.includes('/notes/')) {
            return 'Notes Page';
        }
        if (path.includes('/caregiver')) {
            return 'Home';
        }

        return 'Unknown';
    };
}
